/**
 * Helpers both the chart and the what-if need, pulled out so neither has to import the other.
 *
 * Nothing in here knows what a battery, a tariff or a solar array is. It is series arithmetic and
 * colour arithmetic, and that is the test for whether something belongs in this file.
 */
import { ResourceSeriesVM } from '@models/backend-generated-models/models';

/** Signed total of a series over the points a predicate accepts. */
export function sumSeries(s: ResourceSeriesVM, keep: (v: number) => boolean): number {
  let total = 0;
  for (const p of s.points ?? []) {
    const v = p.value ?? 0;
    if (keep(v)) total += v;
  }
  return total;
}

/**
 * SSRS's BrightPastel palette, the one its charts fell back on for a series with no colour set.
 */
const PALETTE = ['#418cf0', '#fcb441', '#e0400a', '#056492', '#bfbfbf', '#1a3b69', '#ffe382', '#129cdd',
  '#ca6b4b', '#005cdb', '#f3d288', '#506381', '#f1b9a8', '#e0830a', '#7893be'];

/**
 * The series ready to draw: a colour for each that has none, and a name no other series has.
 *
 * Colours come from the palette SSRS used, skipping the ones already taken. Every one of them used to
 * be the same grey: ten uncoloured policies on one chart could not be told apart, and hovering one
 * meter in the list lit up all ten.
 *
 * Names are made unique because the bars, the colours, the legend and the meter list all key on the
 * name, as ngx-charts does. Policy names are not unique on production, and two series of one name
 * drew as one zig-zag line with one colour and one row in the list. The second becomes "Name (2)".
 */
export function withColours(series: ResourceSeriesVM[]): ResourceSeriesVM[] {
  const taken = new Set(series.map(s => (s.rgb || '').toLowerCase()).filter(Boolean));
  const free = PALETTE.filter(c => !taken.has(c));
  let next = 0;
  const seen = new Map<string, number>();
  return series.map(s => {
    const base = s.name ?? '';
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    const name = n > 1 ? `${base} (${n})` : base;
    const rgb = s.rgb || (free.length ? free[next++ % free.length] : PALETTE[next++ % PALETTE.length]);
    return name === s.name && rgb === s.rgb ? s : { ...s, name, rgb };
  });
}

/**
 * Words that name a generation meter, and words that name a grid connection, matched as whole words.
 *
 * They were bare substrings until 13/10/26, so "Wind Lobby Heater" and "Window AC" were generation
 * ("wind"), and "Supply Fan", "Remains Store" and "Grid Ref" were the grid ("supply", "mains",
 * "grid"). A chart of a mains and a lobby heater then said "Solar generated" (see series-util.spec).
 *
 * A word ends at anything that is not a letter, so "Mains_Current" and "Solar_PV" still match. PV is
 * matched with or without its dots ("PV", "P.V."). "Inverter" alone is not generation: a heat pump
 * has one too, and a solar inverter is named for its solar. "Supply" alone is not the grid: on
 * production it is mostly a sub supply ("Kitchen Supply", "Crane Supply"); a site's, building's,
 * total, incoming or HV supply is. "Main" is the grid only beside meter, supply or incomer, so "Main
 * Building" is not.
 *
 * Checked against every logical group name on production (4,157 of them) against the old rules.
 */
const GENERATION = /(?<![a-z])solar(?![a-z])|(?<![a-z0-9])p\.?v(?![a-z0-9])|photovolt|(?<![a-z])wind\s*(?:turbines?|farm|gen[a-z]*)(?![a-z])|(?<![a-z])chp(?![a-z])|(?<![a-z])generat(?:or|ors|ion|ed)(?![a-z])/i;
const GRID = new RegExp([
  '(?<![a-z])mains(?![a-z])',
  '(?<![a-z])main(?![a-z])(?=.*(?<![a-z])(?:supply|incomer|meter)(?![a-z]))',
  '(?<![a-z])grid(?![a-z])(?!\\s*ref)',
  '(?<![a-z])incomers?(?![a-z])',
  '(?<![a-z])import(?:ed)?(?![a-z])',
  '(?<![a-z])(?:esb|mrso)(?![a-z])',
  '(?<![a-z])(?:total|site|incoming|building)(?![a-z])(?=.*(?<![a-z])supply(?![a-z]))',
  '(?<![a-z])hv\\s+supply(?![a-z])',
  '(?<![a-z])supply\\s+(?:meter|point|after)(?![a-z])'
].join('|'), 'i');

/** A generation meter by its name. "Total Site Electricity (CHP + Mains)" is not one. */
export function isGenerationName(name: string | null | undefined): boolean {
  return GENERATION.test(name ?? '') && !GRID.test(name ?? '');
}

/**
 * Which series is the grid, or undefined where the chart is not one grid connection and its
 * generation.
 *
 * Told, or read off the names: exactly one series that is not a generation meter, named as a grid
 * connection or the site's electricity, beside one or more named as generation. Anything else has no
 * grid, and the chart shows each series by its own name rather than an import and export split.
 *
 * This used to take any series with a negative value as the grid, "because only a grid meter
 * exports", and the biggest series failing that. On production a solar meter reads a little below
 * zero overnight (Aran Woolen Mills' Solar PV (Lodge Road), 270 hours down to -0.9 kWh) while a pulse
 * mains meter never goes negative at all, so Lodge Road's solar was taken for the grid: the tiles
 * said 739 kWh imported and 463 generated on a day that was the other way round (Jayden, 07/10/26).
 * One glitch reading on a sub meter did the same to whole sub meter charts, and the size guess named
 * the bigger of any two cold rooms the grid.
 */
export function pickGrid(
  series: ResourceSeriesVM[],
  gridLogicalGroupId?: number
): ResourceSeriesVM | undefined {
  if (series.length < 2) return undefined;

  const told = series.find(s => s.logicalGroupId === gridLogicalGroupId);
  if (told) return told;

  const rest = series.filter(s => !isGenerationName(s.name));
  if (rest.length !== 1) return undefined;

  // And it has to be electricity: "Mains Water" and "Gas Mains" are mains too.
  const name = rest[0].name ?? '';
  if (NOT_ELECTRIC.test(name)) return undefined;
  return GRID.test(name) || /\belectricity\b/i.test(name) ? rest[0] : undefined;
}

/** Not electricity, whatever unit it is read in. */
const NOT_ELECTRIC = /\bgas\b|\boil\b|\blpg\b|\bwater\b|\bheat(?:ing)?\b|\bthermal\b|\bbiomass\b|\bsteam\b/i;

/**
 * The site's electricity mains on this chart, or undefined where the chart does not show it.
 *
 * Jayden, 08/10/26: the what-if goes on any electricity chart that covers the mains, solar or no
 * solar. So beyond the grid and generation pair pickGrid finds, this also takes a chart of the mains
 * on its own, and a remainder chart, whose total row is what is left of the mains after its sub
 * meters (the stack still adds up to the mains, so the mains is the stack).
 */
export function pickMains(
  series: ResourceSeriesVM[],
  gridLogicalGroupId?: number
): ResourceSeriesVM | undefined {
  const grid = pickGrid(series, gridLogicalGroupId);
  if (grid) return grid;

  const rest = series.filter(s => !isGenerationName(s.name));

  // Only the mains itself is checked for being gas, water and so on. An asset on a stacked chart
  // called "Water Heater" is still electricity, and must not take the what-if off the whole chart.
  const total = rest.find(s => s.isTotal);
  if (total) return NOT_ELECTRIC.test(total.name ?? '') ? undefined : total;

  if (rest.length !== 1) return undefined;
  const name = rest[0].name ?? '';
  if (NOT_ELECTRIC.test(name)) return undefined;
  return GRID.test(name) || /\belectric(?:ity|al)?\b/i.test(name) ? rest[0] : undefined;
}

/**
 * Span the bars cover, in days, counting the last bucket rather than stopping at its start.
 *
 * Used to annualise. Measured off the bucket STARTS and their spacing rather than off a row count,
 * because a gap-free 240 days and a patchy 240 rows spread over a year are very different
 * denominators and only the first one annualises honestly.
 */
export function bucketWindowDays(bucketDates: Date[]): number {
  if (!bucketDates.length) return 0;
  if (bucketDates.length === 1) return 1;

  const first = bucketDates[0].getTime();
  const last = bucketDates[bucketDates.length - 1].getTime();
  const gap = (last - first) / (bucketDates.length - 1);

  return Math.max(1, Math.round((last - first + gap) / 86400000));
}

/** Accepts the two forms the palette actually hands us, #rgb/#rrggbb and rgb()/rgba(). */
export function parseRgb(rgb: string | null): number[] | null {
  if (!rgb) return null;
  const s = rgb.trim();
  let parts: number[] | null = null;

  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(s);
  if (hex) {
    const h = hex[1].length === 3 ? hex[1].replace(/./g, ch => ch + ch) : hex[1];
    parts = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16));
  } else {
    const fn = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i.exec(s);
    if (fn) parts = [1, 2, 3].map(i => Number(fn[i]));
  }

  return !parts || parts.some(n => isNaN(n)) ? null : parts;
}

export function toHex(parts: number[]): string {
  return '#' + parts.map(n => {
    const v = Math.round(Math.min(255, Math.max(0, n)));
    return (v < 16 ? '0' : '') + v.toString(16);
  }).join('');
}

/** Scales toward black, so the result keeps its hue rather than going muddy. */
export function darken(rgb: string | null, by: number): string | null {
  const parts = parseRgb(rgb);
  if (!parts) return null;

  const k = 1 - Math.min(1, Math.max(0, by));
  return toHex(parts.map(n => n * k));
}

/** Blends toward white, so the result keeps the hue rather than washing to grey. */
export function lighten(rgb: string | null, by: number): string | null {
  const parts = parseRgb(rgb);
  if (!parts) return null;

  const k = Math.min(1, Math.max(0, by));
  return toHex(parts.map(n => n + (255 - n) * k));
}
